import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Coffee, LogOut, QrCode, User, Crown, X, Sparkles } from 'lucide-react';
import { authService } from '../services/authService';
import { Button } from '../components/ui/button';
import QRCode from 'react-qr-code';
import { Toaster, toast } from 'sonner';

const MemberProfile = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showQR, setShowQR] = useState(false);
    const [prevStamps, setPrevStamps] = useState(0);

    // Initial fetch
    useEffect(() => {
        const fetchData = async () => {
            if (!authService.isAuthenticated()) {
                navigate('/');
                return;
            }

            try {
                const userRes = await authService.me();
                setUser(userRes.data.user);
                setPrevStamps(userRes.data.user.current_stamps);
            } catch (err) {
                console.error('Failed to fetch member data:', err);
                if (err.response && err.response.status === 401) {
                    authService.logout();
                    navigate('/');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [navigate]);

    // Polling for real-time updates (every 3 seconds)
    useEffect(() => {
        if (!user) return;

        const pollInterval = setInterval(async () => {
            try {
                const userRes = await authService.me();
                const newData = userRes.data.user;

                setUser(prevUser => {
                    // Check for stamp increase
                    if (newData.current_stamps > prevStamps) {
                        // Play success sound or vibrate if possible
                        if (navigator.vibrate) navigator.vibrate([100, 50, 100]);

                        // triggers toaster
                        toast.success(`You received a new stamp! (Total: ${newData.current_stamps})`, {
                            duration: 4000,
                            position: 'top-center',
                            style: {
                                background: '#31415A',
                                color: '#F7F1CD',
                                border: '1px solid #F7F1CD',
                            }
                        });
                    }
                    return newData;
                });
                setPrevStamps(newData.current_stamps);

            } catch (err) {
                // Silent error on poll fail
            }
        }, 3000);

        return () => clearInterval(pollInterval);
    }, [user, prevStamps]);

    const handleLogout = async () => {
        await authService.logout();
        navigate('/');
        window.location.reload();
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#31415A]"></div>
            </div>
        );
    }

    const stampCount = user?.current_stamps || 0;

    const maxStamps = 6;
    const qrValue = user?.phone;
    const progress = (stampCount / maxStamps) * 100;

    return (
        <div className="min-h-screen bg-[#F5F5F0] pt-24 pb-12 font-sans relative overflow-hidden">
            <Toaster />
            {/* Background Pattern similar to Home */}
            <div
                className="absolute inset-0 z-0 opacity-40 pointer-events-none"
                style={{
                    backgroundImage: 'url(/bgpaper.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
            />

            {/* Check if user data is loaded before rendering name */}
            <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 relative z-10">
                {/* Header / Greeting */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-12 animate-fade-in">
                    <div className="space-y-4 text-center md:text-left">
                        <h1 className="text-3xl md:text-6xl font-bold text-[#31415A] tracking-tight leading-[1.1]">
                            Hello, <br className="hidden md:block" />
                            <span className="text-[#31415A]">{user?.name || 'Coffee Lover'}</span>
                        </h1>
                        <p className="text-lg text-gray-600 max-w-md">
                            Welcome back to Iraga Coffee. You are <span className="font-bold text-[#31415A]"> {Math.max(0, maxStamps - stampCount)} stamps away</span> from your next reward.
                        </p>
                    </div>

                    <div className="mt-6 md:mt-0 flex items-center gap-4">
                        <Button
                            onClick={handleLogout}
                            variant="outline"
                            className="bg-white/50 backdrop-blur-sm border-[#31415A]/20 text-[#31415A] hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors rounded-xl px-6"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </Button>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-start">

                    {/* LEFT COLUMN: Membership Card */}
                    <div className="relative group perspective-1000 animate-slide-up" style={{ animationDelay: '0.1s' }}>
                        {/* Card Container with Tilt/Hover Effect */}
                        <div className="relative transform transition-all duration-500 hover:scale-[1.02] hover:-rotate-1">
                            {/* Abstract Glow Behind */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-[#31415A] to-[#4a6fa5] rounded-[2rem] blur opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>

                            {/* Actual Card */}
                            <div className="relative bg-[#31415A] rounded-[1.7rem] overflow-hidden shadow-2xl p-8 md:p-10 text-white min-h-[400px] flex flex-col justify-between">
                                {/* Decorative Circles */}
                                <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-white opacity-5 rounded-full blur-3xl"></div>
                                <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-60 h-60 bg-black opacity-20 rounded-full blur-3xl"></div>

                                {/* Card Header */}
                                <div className="flex justify-between items-start z-10">
                                    <div className="space-y-1">
                                        <div className="flex items-center space-x-2">
                                            <Crown className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                                            <span className="text-sm font-bold tracking-widest text-yellow-400 uppercase">Member</span>
                                        </div>
                                        <p className="text-white/60 font-mono tracking-widest text-sm">{user?.phone}</p>
                                    </div>
                                    <Coffee className="w-12 h-12 text-white/90" strokeWidth={1} />
                                </div>

                                {/* Stamp Grid */}
                                <div className="grid grid-cols-4 sm:grid-cols-5 gap-3 sm:gap-4 md:gap-6 my-8 z-10 relative">
                                    {[...Array(maxStamps)].map((_, i) => (
                                        <div key={i} className="relative flex justify-center">
                                            <div className={`
                                                aspect-square w-full max-w-[60px] rounded-full flex items-center justify-center transition-all duration-500
                                                ${i < stampCount
                                                    ? 'bg-white text-[#31415A] shadow-[0_0_20px_rgba(255,255,255,0.4)] scale-100'
                                                    : 'bg-white/10 text-white/20 border border-white/10 scale-90'
                                                }
                                            `}>
                                                <Coffee className={`w-[60%] h-[60%] transition-all duration-500 ${i < stampCount ? 'opacity-100' : 'opacity-30'}`} strokeWidth={2.5} />
                                            </div>

                                            {/* Pulse animation for the next stamp to earn */}
                                            {i === stampCount && (
                                                <div className="absolute inset-0 rounded-full border-2 border-yellow-400/50 animate-ping opacity-75 pointer-events-none"></div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Footer & Progress */}
                                <div className="z-10 mt-auto">
                                    <div className="flex justify-between items-end mb-4">
                                        <div>
                                            <p className="text-3xl md:text-5xl font-bold tracking-tighter">{stampCount}<span className="text-xl md:text-2xl opacity-50 font-normal">/{maxStamps}</span></p>
                                            <p className="text-sm text-white/60 uppercase tracking-widest font-medium">Stamps Collected</p>
                                        </div>
                                        {stampCount >= maxStamps && (
                                            <div className="bg-yellow-400 text-[#31415A] px-4 py-2 rounded-xl font-bold text-sm shadow-xl flex items-center animate-bounce">
                                                <Sparkles className="w-4 h-4 mr-2" />
                                                Reward Available!
                                            </div>
                                        )}
                                    </div>
                                    <div className="h-2 w-full bg-black/20 rounded-full overflow-hidden backdrop-blur-sm">
                                        <div
                                            className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full transition-all duration-1000 ease-out"
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* RIGHT COLUMN: Action / QR */}
                    <div className="space-y-6 animate-slide-up" style={{ animationDelay: '0.2s' }}>
                        {/* Context Widget */}
                        <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 rounded-full bg-[#f0ebe0] flex items-center justify-center">
                                    <User className="w-6 h-6 text-[#31415A]" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-[#31415A] text-lg">Member Benefits</h3>
                                    <p className="text-gray-500 text-sm">Exclusive perks just for you</p>
                                </div>
                            </div>
                            <ul className="space-y-3">
                                <li className="flex items-center text-gray-600 text-sm">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-3" />
                                    Collect {maxStamps} stamps for a free coffee
                                </li>
                                <li className="flex items-center text-gray-600 text-sm">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-3" />
                                    Member-only seasonal offers
                                </li>
                                <li className="flex items-center text-gray-600 text-sm">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-3" />
                                    Priority access to new beans
                                </li>
                            </ul>
                        </div>

                        {/* Show QR Button / QR Display */}
                        <div className="transition-all duration-500 ease-in-out">
                            {!showQR ? (
                                <button
                                    onClick={() => setShowQR(true)}
                                    className="w-full bg-white group hover:bg-[#31415A] border-2 border-[#31415A] rounded-[2rem] p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-between"
                                >
                                    <div className="flex items-center gap-6">
                                        <div className="p-4 bg-gray-50 group-hover:bg-white/10 rounded-2xl transition-colors">
                                            <QrCode className="w-8 h-8 text-[#31415A] group-hover:text-white" />
                                        </div>
                                        <div className="text-left">
                                            <h3 className="font-bold text-2xl text-[#31415A] group-hover:text-white transition-colors">Show QR Code</h3>
                                            <p className="text-gray-500 group-hover:text-white/70 transition-colors">Scan at cashier to collect stamps</p>
                                        </div>
                                    </div>
                                    <div className="w-10 h-10 rounded-full border-2 border-[#31415A]/20 group-hover:border-white/30 flex items-center justify-center transition-colors">
                                        <span className="text-xl group-hover:text-white text-[#31415A]">→</span>
                                    </div>
                                </button>
                            ) : (
                                <div className="bg-white rounded-[2rem] p-8 shadow-2xl border border-gray-100 text-center relative overflow-hidden animate-in fade-in zoom-in duration-300">
                                    <button
                                        onClick={() => setShowQR(false)}
                                        className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                                    >
                                        <X className="w-6 h-6" />
                                    </button>

                                    <div className="py-6">
                                        <h3 className="text-2xl font-bold text-[#31415A] mb-2">Scan Me</h3>
                                        <p className="text-gray-500 mb-2">Show this QR to the barista</p>
                                        <p className="text-xs text-amber-600 font-bold uppercase tracking-wider mb-6 bg-amber-50 inline-block px-3 py-1 rounded-full">
                                            Scan to Collect Stamps or Redeem Reward
                                        </p>

                                        <div className="bg-white p-4 rounded-3xl border-[3px] border-dashed border-[#31415A]/20 inline-block shadow-inner mb-6">
                                            <QRCode
                                                value={qrValue || ''}
                                                size={220}
                                                level="H"
                                                fgColor="#31415A"
                                            />
                                        </div>

                                        <div className="flex justify-center items-center space-x-2 text-gray-500 text-sm font-mono bg-gray-100 py-3 px-6 rounded-xl mx-auto w-fit">
                                            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                            <span>{user?.phone}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>

            <style jsx>{`
                @keyframes fade-in {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in {
                    animation: fade-in 0.6s ease-out forwards;
                }
                 @keyframes slide-up {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-slide-up {
                    animation: slide-up 0.6s ease-out forwards;
                    opacity: 0;
                }
                .perspective-1000 {
                    perspective: 1000px;
                }
            `}</style>
        </div>
    );
};

export default MemberProfile;
